import type {
  DesktopPraxaResourcesBridge,
  PraxaDesktopConversationMutationResult,
  PraxaDesktopConversationMutationState,
  PraxaDesktopResourceIndexState,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  applyConversationMutationResult,
  applyConversationMutationStateResult,
  conversationMutationResultMessage,
  isConversationMutationPending,
} from "./desktopPraxaConversationMutations";

export * from "./desktopPraxaHistory";

export const PRAXA_RESOURCES_LOAD_ERROR =
  "Praxa history couldn’t load. Local coding is still available.";
export const PRAXA_MUTATIONS_LOAD_ERROR =
  "Conversation changes couldn’t load. Saved history remains available.";
export const PRAXA_MUTATION_ACTION_ERROR =
  "This change couldn’t be saved. Your entry is still here; try again.";

function getBridge(): DesktopPraxaResourcesBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaResources;
}

function supportsConversationMutations(bridge: DesktopPraxaResourcesBridge): boolean {
  const candidate = bridge as Partial<DesktopPraxaResourcesBridge>;
  return (
    typeof candidate.getConversationMutationState === "function" &&
    typeof candidate.retryConversationMutations === "function" &&
    typeof candidate.createConversation === "function" &&
    typeof candidate.updateConversation === "function" &&
    typeof candidate.discardConversationMutation === "function" &&
    typeof candidate.onConversationMutationStateChange === "function"
  );
}

export function isDesktopPraxaResourcesAvailable(): boolean {
  return getBridge() !== undefined;
}

export interface PraxaConversationMutationController {
  readonly actionError: string | null;
  readonly available: boolean;
  readonly clearActionError: () => void;
  readonly createConversation: (
    title: string,
  ) => Promise<PraxaDesktopConversationMutationResult | null>;
  readonly discard: (
    conversationId: string,
  ) => Promise<PraxaDesktopConversationMutationState | null>;
  readonly error: string | null;
  readonly isPending: (conversationId: string) => boolean;
  readonly loading: boolean;
  readonly retry: () => Promise<PraxaDesktopConversationMutationState | null>;
  readonly state: PraxaDesktopConversationMutationState | null;
  readonly updateConversation: (
    conversationId: string,
    change: {
      readonly title?: string;
      readonly starred?: boolean;
    },
  ) => Promise<PraxaDesktopConversationMutationResult | null>;
}

export interface DesktopPraxaResourcesController {
  readonly bridge: DesktopPraxaResourcesBridge | undefined;
  readonly error: string | null;
  readonly loading: boolean;
  readonly mutations: PraxaConversationMutationController;
  readonly refresh: () => Promise<void>;
  readonly revision: number;
  readonly state: PraxaDesktopResourceIndexState | null;
}

export function useDesktopPraxaResources(): DesktopPraxaResourcesController {
  const bridge = getBridge();
  const [state, setState] = useState<PraxaDesktopResourceIndexState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const eventVersionRef = useRef(0);
  const [mutationState, setMutationState] = useState<PraxaDesktopConversationMutationState | null>(
    null,
  );
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationActionError, setMutationActionError] = useState<string | null>(null);
  const [mutationLoading, setMutationLoading] = useState(false);
  const mutationEventVersionRef = useRef(0);
  const mutationBridgeAvailable = bridge !== undefined && supportsConversationMutations(bridge);

  const applyState = useCallback((nextState: PraxaDesktopResourceIndexState) => {
    setState(nextState);
    setError(null);
    setRevision((current) => current + 1);
  }, []);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextState = await bridge.refresh();
      if (eventVersionRef.current === eventVersion) applyState(nextState);
    } catch {
      if (eventVersionRef.current === eventVersion) setError(PRAXA_RESOURCES_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [applyState, bridge]);

  const applyMutationState = useCallback((nextState: PraxaDesktopConversationMutationState) => {
    setMutationState(nextState);
    setMutationError(null);
  }, []);

  const reconcileMutationState = useCallback(async () => {
    if (!bridge || !supportsConversationMutations(bridge)) return null;
    const eventVersion = mutationEventVersionRef.current;
    try {
      const nextState = await bridge.getConversationMutationState();
      if (mutationEventVersionRef.current === eventVersion) applyMutationState(nextState);
      return nextState;
    } catch {
      if (mutationEventVersionRef.current === eventVersion) {
        setMutationError(PRAXA_MUTATIONS_LOAD_ERROR);
      }
      return null;
    }
  }, [applyMutationState, bridge]);

  const applyMutationResult = useCallback(
    (result: PraxaDesktopConversationMutationResult) => {
      setMutationActionError(
        result.status === "attention" ? null : conversationMutationResultMessage(result),
      );
      setMutationState((currentState) =>
        applyConversationMutationStateResult(currentState, result),
      );
      if (result.status === "applied") {
        setState((currentState) => applyConversationMutationResult(currentState, result));
        setRevision((current) => current + 1);
      }
      void reconcileMutationState();
      return result;
    },
    [reconcileMutationState],
  );

  const createConversation = useCallback(
    async (title: string): Promise<PraxaDesktopConversationMutationResult | null> => {
      setMutationActionError(null);
      if (!bridge || !supportsConversationMutations(bridge)) {
        setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
        return null;
      }
      try {
        return applyMutationResult(await bridge.createConversation(title));
      } catch {
        setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
        return null;
      }
    },
    [applyMutationResult, bridge],
  );

  const updateConversation = useCallback(
    async (
      conversationId: string,
      change: {
        readonly title?: string;
        readonly starred?: boolean;
      },
    ): Promise<PraxaDesktopConversationMutationResult | null> => {
      setMutationActionError(null);
      if (!bridge || !supportsConversationMutations(bridge)) {
        setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
        return null;
      }
      try {
        return applyMutationResult(await bridge.updateConversation(conversationId, change));
      } catch {
        setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
        return null;
      }
    },
    [applyMutationResult, bridge],
  );

  const retryConversationMutations = useCallback(async () => {
    setMutationActionError(null);
    if (!bridge || !supportsConversationMutations(bridge)) {
      setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
      return null;
    }
    setMutationLoading(true);
    const eventVersion = mutationEventVersionRef.current;
    try {
      const nextState = await bridge.retryConversationMutations();
      if (mutationEventVersionRef.current === eventVersion) applyMutationState(nextState);
      return nextState;
    } catch {
      setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
      return null;
    } finally {
      setMutationLoading(false);
    }
  }, [applyMutationState, bridge]);

  const discardConversationMutation = useCallback(
    async (conversationId: string) => {
      setMutationActionError(null);
      if (!bridge || !supportsConversationMutations(bridge)) {
        setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
        return null;
      }
      setMutationLoading(true);
      const eventVersion = mutationEventVersionRef.current;
      try {
        const nextState = await bridge.discardConversationMutation(conversationId);
        if (mutationEventVersionRef.current === eventVersion) applyMutationState(nextState);
        return nextState;
      } catch {
        setMutationActionError(PRAXA_MUTATION_ACTION_ERROR);
        return null;
      } finally {
        setMutationLoading(false);
      }
    },
    [applyMutationState, bridge],
  );

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((nextState) => {
        eventVersionRef.current += 1;
        if (active) applyState(nextState);
      });
    } catch {
      setError(PRAXA_RESOURCES_LOAD_ERROR);
    }

    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((nextState) => {
        if (active && eventVersionRef.current === eventVersion) applyState(nextState);
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setError(PRAXA_RESOURCES_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  useEffect(() => {
    setMutationActionError(null);
    if (!bridge || !supportsConversationMutations(bridge)) {
      setMutationState(null);
      setMutationError(null);
      setMutationLoading(false);
      return;
    }

    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onConversationMutationStateChange((nextState) => {
        mutationEventVersionRef.current += 1;
        if (active) applyMutationState(nextState);
      });
    } catch {
      setMutationError(PRAXA_MUTATIONS_LOAD_ERROR);
    }

    const eventVersion = mutationEventVersionRef.current;
    setMutationLoading(true);
    void Promise.resolve()
      .then(() => bridge.getConversationMutationState())
      .then((nextState) => {
        if (active && mutationEventVersionRef.current === eventVersion) {
          applyMutationState(nextState);
        }
      })
      .catch(() => {
        if (active && mutationEventVersionRef.current === eventVersion) {
          setMutationError(PRAXA_MUTATIONS_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setMutationLoading(false);
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyMutationState, bridge]);

  const mutationsAvailable =
    mutationBridgeAvailable &&
    mutationState !== null &&
    mutationState.status !== "revoked" &&
    mutationState.status !== "unavailable" &&
    mutationState.status !== "syncing" &&
    !mutationLoading;

  return {
    bridge,
    error,
    loading,
    mutations: {
      actionError: mutationActionError,
      available: mutationsAvailable,
      clearActionError: () => setMutationActionError(null),
      createConversation,
      discard: discardConversationMutation,
      error: mutationError,
      isPending: (conversationId) => isConversationMutationPending(mutationState, conversationId),
      loading: mutationLoading,
      retry: retryConversationMutations,
      state: mutationState,
      updateConversation,
    },
    refresh,
    revision,
    state,
  };
}
